// functions to manage files for the linux gateways that we override in order to control things like wifi, I/O configuration, etc.
// file override settings go in gateway.settings (per-gateway) or in the template.gtemplate.settings (per-template)

// used to search through and delete old settings when saving
export const tags = {
  nm_wifi: 'nm_wifi',
  stw_wake: 'stw_wake',
  stw_can: 'stw_can',
};

// used to search through and find existing UI parameters when loading
export const file_types = {
  wifi_nmconnection_enable_file: 'wifi_nmconnection_enable_file',
  wifi_nmconnection_file: 'wifi_nmconnection_file',
  stw_wake_file: 'stw_wake_file',
  stw_can_file: 'stw_can_file',
};

const wifiStwEnableFilePath = '/etc/wlan.conf';
const wifiClientFilePath =
  '/etc/NetworkManager/system-connections/wificlient.nmconnection';

export function getNetworkManagerWifiEnabled(params) {
  return [
    {
      ui: {
        file_type: file_types.wifi_nmconnection_enable_file,
        tag: tags.nm_wifi,
      },
      path: wifiStwEnableFilePath,
      permissions: '644',
      contents: Buffer.from(
        `# /etc/wlan.conf
#
# ----------------------------------------------------------------
# Configuration file for the wireless local area network interface
# ----------------------------------------------------------------

# Enable or disable wlan interface
# WLAN_ENABLE=ON --> activate, else deactivate
# Default: WLAN_ENABLE=OFF
WLAN_ENABLE=ON`
      ).toString('base64'),
    },
    {
      ui: {
        file_type: file_types.wifi_nmconnection_file,
        tag: tags.nm_wifi,
        params: {
          stwWiFiControl: true,
          stwWiFiEnabled: true,
          WiFissid: params.WiFissid,
          WiFipassword: params.WiFipassword,
        },
      },
      path: wifiClientFilePath,
      permissions: '600',
      contents: Buffer.from(
        `[connection]
id=wificlient
uuid=689ed0a4-8e00-4f99-b90d-635b4e628791
type=wifi
interface-name=wlan0
permissions=

[wifi]
band=bg
mac-address-blacklist=
mode=infrastructure
ssid=${params.WiFissid}

[wifi-security]
key-mgmt=wpa-psk
psk=${params.WiFipassword}

[ipv4]
# this puts it below cell and ethernet at 100
route-metric=50 
dns-search=
method=auto

[ipv6]
addr-gen-mode=stable-privacy
dns-search=
method=auto

[proxy]`
      ).toString('base64'),
    },
  ];
}

export function getNetworkManagerWifiDisabled() {
  return [
    {
      ui: {
        file_type: file_types.wifi_nmconnection_enable_file,
        tag: tags.nm_wifi,
        params: {
          stwWiFiControl: true, // we're still controlling wifi, we just have it disabled. we need this state to turn off wifi on gateways that previously had it on
          stwWiFiEnabled: false,
        },
      },
      path: wifiStwEnableFilePath,
      permissions: '644',
      contents: Buffer.from(
        `# /etc/wlan.conf
#
# ----------------------------------------------------------------
# Configuration file for the wireless local area network interface
# ----------------------------------------------------------------

# Enable or disable wlan interface
# WLAN_ENABLE=ON --> activate, else deactivate
# Default: WLAN_ENABLE=OFF
WLAN_ENABLE=OFF`
      ).toString('base64'),
    },
    {
      ui: {
        file_type: file_types.wifi_nmconnection_file,
        tag: tags.nm_wifi,
      },
      path: wifiClientFilePath,
      permissions: '600',
      contents: Buffer.from(`#disabled`).toString('base64'),
    },
  ];
}

const stwWakeFilePath = '/etc/wakeup.conf';

export function getStwWakeFileEnabled(params) {
  return [
    {
      ui: {
        file_type: file_types.stw_wake_file,
        tag: tags.stw_wake,
        params: {
          stwWakeControl: true,
          stwWakeEnabled: true,
          stwWakeInterval_s: params.stwWakeInterval_s,
          stwWakeRunTime_s: params.stwWakeRunTime_s,
        },
      },
      path: stwWakeFilePath,
      permissions: '644',
      contents: Buffer.from(
        `# /etc/wakeup.conf
#
# -----------------------------------------
# wakeup settings
# -----------------------------------------

# Ignition
# --------
# Run time in seconds after shutdown (ignition off).
# Set the default value here or wakeup-reason depending on:
# Ignition wakeup time in second, default is 0.
SD_RUN_TIME_IGN=0

# Motion
# ------
# Activate motion wakeup with "ON", else deactivate
# Default: MOTION_WAKEUP=OFF
MOTION_WAKEUP=OFF

# Run time in seconds after motion wakeup.
# Default: SD_RUN_TIME_MOT=300
SD_RUN_TIME_MOT=300

# RTC
# ---
# Activate rtc wakeup with "after_sec", else deactivate
# Enable wakeup after seconds with "after_sec" or
# Enable date + time wakeup with "tomorrow_at".
# Default: RTC_WAKEUP=OFF
RTC_WAKEUP=after_sec

# In combination with "tomorrow_at": RTC_WAKEUP_TIME hold the value in hh[:mm[:ss]].
# In combination with "after_sec": RTC_WAKEUP_TIME holds the value in seconds.
# Note:
# The wakeup time will be rounded up to a full minute: So if RTC_WAKEUP="after_sec"
# and RTC_WAKEUP_TIME is set to 1, it means that the system will wake up
# after 1 second or at least after 59 seconds.
# Default: RTC_WAKEUP_TIME=300
RTC_WAKEUP_TIME=${params.stwWakeInterval_s}

# Run time in seconds after rtc wakeup.
# Default: SD_RUN_TIME_RTC=300
SD_RUN_TIME_RTC=${params.stwWakeRunTime_s}
`
      ).toString('base64'),
    },
  ];
}

export function getStwWakeFileDisabled() {
  return [
    {
      ui: {
        file_type: file_types.stw_wake_file,
        tag: tags.stw_wake,
        params: {
          stwWakeControl: true, // we're still controlling wake, we just have it disabled. we need this state to turn off wake on gateways that previously had it on
          stwWakeEnabled: false,
        },
      },
      path: stwWakeFilePath,
      permissions: '644',
      contents: Buffer.from(
        `# /etc/wakeup.conf
#
# -----------------------------------------
# wakeup settings
# -----------------------------------------

# Ignition
# --------
# Run time in seconds after shutdown (ignition off).
# Set the default value here or wakeup-reason depending on:
# Ignition wakeup time in second, default is 0.
SD_RUN_TIME_IGN=0

# Motion
# ------
# Activate motion wakeup with "ON", else deactivate
# Default: MOTION_WAKEUP=OFF
MOTION_WAKEUP=OFF

# Run time in seconds after motion wakeup.
# Default: SD_RUN_TIME_MOT=300
SD_RUN_TIME_MOT=300

# RTC
# ---
# Activate rtc wakeup with "after_sec", else deactivate
# Enable wakeup after seconds with "after_sec" or
# Enable date + time wakeup with "tomorrow_at".
# Default: RTC_WAKEUP=OFF
RTC_WAKEUP=OFF

# In combination with "tomorrow_at": RTC_WAKEUP_TIME hold the value in hh[:mm[:ss]].
# In combination with "after_sec": RTC_WAKEUP_TIME holds the value in seconds.
# Note:
# The wakeup time will be rounded up to a full minute: So if RTC_WAKEUP="after_sec"
# and RTC_WAKEUP_TIME is set to 1, it means that the system will wake up
# after 1 second or at least after 59 seconds.
# Default: RTC_WAKEUP_TIME=300
RTC_WAKEUP_TIME=86400

# Run time in seconds after rtc wakeup.
# Default: SD_RUN_TIME_RTC=300
SD_RUN_TIME_RTC=300
`
      ).toString('base64'),
    },
  ];
}

export function getStwCANsettings(params) {
  // UI options for CAN are "125k", "250k", "500k", "1M"
  // can values in this file look like "250000" (250k) or "500000" (500k)
  // the UI selector already translates between them with BITRATE_OPTIONS[]

  // the newest stw BSP (late 2023) uses two files depending on the model: /etc/.can.conf.imx_silent and /etc/.can.conf.imx_silent_xmc_silent
  // BUT we should be able to just write out to the symlink /etc/can.conf and have that write to the actual file
  // this has the advantage of possibly working on older images too

  // if these are missing, set them, the file controls all four
  if (!params.bitrateCan0Val) {
    params.bitrateCan0Val = '250000';
  }
  if (!params.bitrateCan1Val) {
    params.bitrateCan1Val = '250000';
  }
  if (!params.bitrateCan2Val) {
    params.bitrateCan2Val = '250000';
  }
  if (!params.bitrateCan3Val) {
    params.bitrateCan3Val = '250000';
  }

  return [
    {
      ui: {
        file_type: file_types.stw_can_file,
        tag: tags.stw_can,
        params,
      },
      path: '/etc/can.conf',
      permissions: '644', // rw,r,r
      contents: Buffer.from(
        `# /etc/can.conf (set by elevat logger file overrides)
#
# -----------------------------------------------------------------------------
# Configuration file for the CAN interfaces
# -----------------------------------------------------------------------------

# CAN 0 ON activate, else deactivate
CAN0_ENABLE=ON
# Baud rate for CAN0. Assign one or multiple baud rates, or activate auto
# baud rate detection, see section below.
CAN0_BAUDRATE=${params.bitrateCan0Val}
# Length of the tx queue CAN0
# Default: 1000
#CAN0_TXQUEUELENGTH=1000
# CAN 0 Silent mode.
# ON active, else deactivate
CAN0_SILENT=OFF

# CAN 1 ON activate, else deactivate
CAN1_ENABLE=ON
# Baud rate for CAN1. Assign one or multiple baud rates, or activate auto
# baud rate detection, see section below.
CAN1_BAUDRATE=${params.bitrateCan1Val}
# Length of the tx queue CAN1
# Default: 1000
#CAN1_TXQUEUELENGTH=1000
# CAN 1 Silent mode.
# ON active, else deactivate
CAN1_SILENT=OFF

# CAN 2 ON activate, else deactivate
CAN2_ENABLE=ON
# Baud rate for CAN2. Assign one or multiple baud rates, or activate auto
# baud rate detection, see section below.
CAN2_BAUDRATE=${params.bitrateCan2Val}
# Length of the tx queue CAN2
# Default: 1000
#CAN2_TXQUEUELENGTH=1000
# CAN 2 Silent mode.
# ON active, else deactivate
CAN2_SILENT=OFF

# CAN 3 ON activate, else deactivate
CAN3_ENABLE=ON
# Baud rate for CAN3. Assign one or multiple baud rates, or activate auto
# baud rate detection, see section below.
CAN3_BAUDRATE=${params.bitrateCan3Val}
# Length of the tx queue CAN3
# Default: 1000
#CAN3_TXQUEUELENGTH=1000
# CAN 3 Silent mode.
# ON active, else deactivate
CAN3_SILENT=OFF

# -----------------------------------------------------------------------------
# End of configuration
# -----------------------------------------------------------------------------

# Most of the above described configuration settings are not very complex and
# do not need further explanation. The only exception is the CANx_BAUDRATE
# variable. This variable offers multiple option with different functionalities:
#
# * Assign one fixed baud rate that will be initialized, there are no checks
#   performed that will tests of any messages can be received with this baud
#   rate. Therefore, the initialization process will pass independent of the
#   traffic on the bus. This process is very fast.
#     e.g. CAN0_BAUDRATE=250000
#
# * Assign multiple baud rates that will be tested at every boot. The CAN
#   interface will only be initialized if the baud rate can be verified by
#   receiving real CAN messages. The testing is performed in SILENT mode.
#   This process can take several seconds until the correct baud rate is
#   determined.
#     e.g. CAN0_BAUDRATE="125000 250000 500000"
#
# * Activate auto baud rate feature. The CAN interface will be tested with
#   multiple baud rates. CAN will only be initialized if the baud rate can be
#   verified by receiving real CAN messages. In that case the detected baud
#   rate overwrites the CANx_BAUDRATE variable in this file. The testing is
#   performed in SILENT mode. This process can take several seconds until the
#   correct baud rate is determined. All system starts after that will be as
#   fast as the fixed baud rate option.
#     e.g. CAN0_BAUDRATE=auto
#
#   NOTE: The detection is performed in SILENT mode. That requires two other
#         active participants on the respective CAN bus for it to work.
`
      ).toString('base64'),
    },
  ];
}

// give these either template.gtemplate.settings.file_overrides or gateway.settings.file_overrides

// removes any settings found with a matching tag so we can redo them when a user saves new settings
export function removeFileOverridesByTag(file_overrides, tag) {
  if (!file_overrides?.length) {
    return [];
  }
  return file_overrides.filter(
    (override) => !override?.ui?.tag || override?.ui?.tag !== tag
  );
}

// loads existing params from settings so we can present them to the user on open
export function getParamsByFileType(file_overrides, file_type) {
  if (!Array.isArray(file_overrides)) return {};

  const override = file_overrides.find((override) => {
    return override?.ui?.file_type === file_type;
  });
  return override?.ui?.params || {};
}
